import api from './api';
import { AccessService, DirectoryUser } from './access';

export interface User {
  id: number;
  username: string;
  email: string;
  first_name: string;
  last_name: string;
  full_name: string;
  employee_id?: string;
  department?: string;
  plant?: string;
  phone?: string;
  avatar?: string;
  is_active: boolean;
  is_staff: boolean;
  is_admin: boolean;
  role_display: string;
  date_joined: string;
  last_login?: string;
  created_at: string;
  updated_at: string;
  viewable_plants?: Array<{
    id: number;
    alv_id: string | null;
    code: string;
    name: string;
    is_active: boolean;
  }>;
}

export interface UserCreate {
  username: string;
  email: string;
  first_name: string;
  last_name: string;
  employee_id?: string;
  department?: string;
  plant?: string;
  phone?: string;
  is_active?: boolean;
  is_staff?: boolean;
  password?: string;
  confirm_password?: string;
  viewable_plant_ids?: number[];
}

export interface UserUpdate {
  email: string;
  first_name: string;
  last_name: string;
  employee_id?: string;
  department?: string;
  plant?: string;
  phone?: string;
  is_active: boolean;
  is_staff: boolean;
  viewable_plant_ids?: number[];
}


export interface UserStats {
  total_users: number;
  active_users: number;
  inactive_users: number;
  admin_users: number;
  normal_users: number;
}

export interface UserFilters {
  search?: string;
  is_active?: boolean;
  is_staff?: boolean;
  department?: string;
  plant?: string;
  page?: number;
  page_size?: number;
  ordering?: string;
}

export interface UserListResponse {
  count: number;
  next: string | null;
  previous: string | null;
  results: User[];
}

const directoryUserToUser = (user: DirectoryUser): User => ({
  id: user.id,
  username: user.username,
  email: '',
  first_name: user.first_name,
  last_name: user.last_name,
  full_name: user.full_name,
  employee_id: undefined,
  department: user.department,
  plant: user.plant,
  phone: undefined,
  avatar: undefined,
  is_active: true,
  is_staff: false,
  is_admin: false,
  role_display: 'User',
  date_joined: '',
  last_login: undefined,
  created_at: '',
  updated_at: '',
});

export class UserService {
  private static baseUrl = '/users';

  static async getUsers(filters: UserFilters = {}): Promise<UserListResponse> {
    const params = new URLSearchParams();
    
    Object.entries(filters).forEach(([key, value]) => {
      if (value !== undefined && value !== null && value !== '') {
        params.append(key, value.toString());
      }
    });

    const response = await api.get(`${this.baseUrl}/?${params.toString()}`);
    return response.data;
  }

  static async getAllUsers(filters: UserFilters = {}): Promise<User[]> {
    // This method is used by mentor/participant selectors throughout the
    // application.  Those selectors need a safe directory, not the
    // administrative `/users/` endpoint (which correctly returns 403 to a
    // normal user).
    if (filters.is_active !== false) {
      const directoryUsers = await AccessService.getAllDirectoryUsers({
        search: filters.search,
        plant: filters.plant,
      });
      return directoryUsers.map(directoryUserToUser);
    }

    const allUsers: User[] = [];
    let page = 1;

    while (true) {
      const response = await this.getUsers({ ...filters, page, page_size: 100 });
      allUsers.push(...response.results);

      if (!response.next) {
        break;
      }

      page += 1;
    }

    return allUsers;
  }

  /** Fetch one directory page. Use getAllDirectoryUsers for selectors and name lookups. */
  static async getDirectoryUsers(filters: {
    search?: string;
    plant?: string;
    page?: number;
    page_size?: number;
  } = {}): Promise<UserListResponse> {
    const response = await AccessService.getDirectoryUsers(filters);
    return {
      ...response,
      results: response.results.map(directoryUserToUser),
    };
  }

  /** Load every page of the active users visible to the caller for workflow selectors. */
  static async getAllDirectoryUsers(filters: {
    search?: string;
    plant?: string;
  } = {}): Promise<User[]> {
    const users = await AccessService.getAllDirectoryUsers(filters);
    return users.map(directoryUserToUser);
  }

  static async getUser(id: number): Promise<User> {
    const response = await api.get(`${this.baseUrl}/${id}/`);
    return response.data;
  }

  static async createUser(userData: UserCreate): Promise<User> {
    const response = await api.post(`${this.baseUrl}/`, userData);
    return response.data;
  }

  static async updateUser(id: number, userData: UserUpdate): Promise<User> {
    const response = await api.patch(`${this.baseUrl}/${id}/`, userData);
    return response.data;
  }

  static async deleteUser(id: number): Promise<void> {
    await api.delete(`${this.baseUrl}/${id}/`);
  }


  static async sendPasswordReset(id: number): Promise<{
    message: string;
    email: string;
  }> {
    const response = await api.post(`${this.baseUrl}/${id}/send_password_reset/`);
    return response.data;
  }

  static async toggleRole(id: number): Promise<{
    message: string;
    user: User;
  }> {
    const response = await api.post(`${this.baseUrl}/${id}/toggle_role/`);
    return response.data;
  }

  static async toggleActive(id: number): Promise<{
    message: string;
    user: User;
  }> {
    const response = await api.post(`${this.baseUrl}/${id}/toggle_active/`);
    return response.data;
  }

  static async getUserStats(): Promise<UserStats> {
    const response = await api.get(`${this.baseUrl}/stats/`);
    return response.data;
  }


  static formatUserRole(user: User): string {
    return user.role_display;
  }

  static formatUserStatus(user: User): string {
    return user.is_active ? 'Active' : 'Inactive';
  }

  static formatLastLogin(lastLogin?: string): string {
    if (!lastLogin) return 'Never';
    
    const date = new Date(lastLogin);
    const now = new Date();
    const diffInHours = (now.getTime() - date.getTime()) / (1000 * 60 * 60);
    
    if (diffInHours < 24) {
      return 'Today';
    } else if (diffInHours < 48) {
      return 'Yesterday';
    } else if (diffInHours < 24 * 7) {
      const days = Math.floor(diffInHours / 24);
      return `${days} day${days > 1 ? 's' : ''} ago`;
    } else {
      return date.toLocaleDateString();
    }
  }
}
